import './Messages.css';
import * as React from 'react';
import {
    Box, Sheet, List, ListItem, ListItemButton, ListItemContent, Avatar,
    Typography, Input, IconButton, Stack, Divider, Button,
    Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Badge
} from '@mui/joy';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import MailIcon from '@mui/icons-material/Mail';
import DoneIcon from '@mui/icons-material/Done';
import Popover from '@mui/material/Popover';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';

// ---------- API + WS base ----------
const API_BASE =
    import.meta.env.VITE_API_BASE ||
    import.meta.env.VITE_API_URL ||
    (typeof window !== 'undefined' ? window.__ENV?.API_BASE : '') ||
    '';

const WS_URL =
    import.meta.env.VITE_WS_URL ||
    API_BASE;

if (!API_BASE) {
    console.error('API_BASE is missing – set VITE_API_BASE (or VITE_API_URL) at build time.');
}

export default function Messages() {
    const [me, setMe] = React.useState({ id: '', name: 'You', avatar: '' });

    // friends and requests
    const [friends, setFriends] = React.useState([]);
    const [incoming, setIncoming] = React.useState([]);
    const [outgoing, setOutgoing] = React.useState([]);
    const [activeId, setActiveId] = React.useState('');

    // chat
    const [draft, setDraft] = React.useState('');
    const [threads, setThreads] = React.useState({});
    const socketRef = React.useRef(null);

    // add friend modal
    const [openAdd, setOpenAdd] = React.useState(false);
    const [addEmail, setAddEmail] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [err, setErr] = React.useState('');

    // navigation
    const navigate = useNavigate();

    // emojis
    const EMOJIS = [
        "😀", "😄", "😁", "😊", "😍", "🥰", "🤣", "😂", "🙂", "😉", "😎",
        "🤔", "🙃", "😴", "🤤", "😢", "😭", "😡", "👏", "👌", "👍", "🙏",
        "🔥", "🌟", "💯", "✅", "❌", "✨", "🎉", "🥳", "📝", "📌", "📷"
    ];
    const [emojiAnchor, setEmojiAnchor] = React.useState(null);
    const inputElRef = React.useRef(null);

    function insertEmojiAtCursor(emo) {
        const el = inputElRef.current;
        if (!el) { setDraft((d) => d + emo); return; }
        const start = el.selectionStart ?? draft.length;
        const end = el.selectionEnd ?? draft.length;
        const next = draft.slice(0, start) + emo + draft.slice(end);
        setDraft(next);
        requestAnimationFrame(() => {
            el.focus();
            el.selectionStart = el.selectionEnd = start + emo.length;
        });
    }

    // ---------- load me + friends + requests ----------
    async function refreshFriends() {
        const [fRes, rqRes] = await Promise.all([
            fetch(`${API_BASE}/chat/friends`, { credentials: 'include' }),
            fetch(`${API_BASE}/chat/requests`, { credentials: 'include' }),
        ]);
        const fr = fRes.ok ? await fRes.json() : [];
        const rq = rqRes.ok ? await rqRes.json() : { incoming: [], outgoing: [] };
        setFriends(fr);
        setIncoming(rq.incoming || []);
        setOutgoing(rq.outgoing || []);
        if (!activeId && fr[0]?.id) setActiveId(fr[0].id);
    }

    React.useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const meRes = await fetch(`${API_BASE}/me`, { credentials: 'include' });
                if (!meRes.ok) throw new Error('me failed');
                const meJson = await meRes.json();
                if (!alive) return;
                setMe({ id: String(meJson.id), name: meJson.email });

                // fetch profile to get pfp
                const profRes = await fetch(`${API_BASE}/profile`, { credentials: 'include' });
                if (profRes.ok) {
                    const prof = await profRes.json();
                    if (!alive) return;
                    setMe(m => ({ ...m, avatar: prof.pfp || '' }));
                }

                await refreshFriends();
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { alive = false; };
    }, []);

    // ---------- socket ----------
    React.useEffect(() => {
        if (!me.id) return;
        const socket = io(WS_URL, {
            withCredentials: true,
            transports: ['websocket', 'polling'],
        });
        socketRef.current = socket;

        socket.on('dm:history', ({ peerId, msgs }) => {
            setThreads((t) => ({ ...t, [peerId]: msgs || [] }));
        });
        socket.on('dm:new', (msg) => {
            const peer = String(msg.from) === String(me.id) ? msg.to : msg.from;
            setThreads((t) => ({ ...t, [peer]: [...(t[peer] || []), msg] }));
        });

        // NEW: ask for history on connect if a thread is already selected
        if (activeId) {
            socket.emit('dm:history', { peerId: activeId });
        }

        return () => {
            socket.off('dm:history');
            socket.off('dm:new');
            socket.disconnect();
        };
    }, [me.id]); // keep simple

    // NEW: Always (re)ask for history when switching peers
    React.useEffect(() => {
        const socket = socketRef.current;
        if (!socket || !activeId) return;
        socket.emit('dm:history', { peerId: activeId });
    }, [activeId]);

    const activeMsgs = threads[activeId] || [];
    const canChat = friends.some((f) => String(f.id) === String(activeId));

    // ---------- actions ----------
    const send = () => {
        const text = draft.trim();
        if (!text || !activeId || !canChat) return;
        socketRef.current?.emit('dm:send', { to: activeId, text });
        setDraft('');
    };

    async function submitAddFriend() {
        if (!addEmail.trim()) return;
        setBusy(true); setErr('');
        try {
            const res = await fetch(`${API_BASE}/chat/friends/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email: addEmail.trim() })
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.error || 'Failed to send request');
            setAddEmail('');
            setOpenAdd(false);
            await refreshFriends();
        } catch (e) {
            setErr(e.message || 'Failed to send request');
        } finally {
            setBusy(false);
        }
    }

    async function acceptRequest(userId) {
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/chat/friends/accept`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ userId })
            });
            if (!res.ok) throw new Error('Failed to accept');
            await refreshFriends();
            setActiveId(String(userId));
        } catch (e) {
            console.error(e);
        } finally {
            setBusy(false);
        }
    }

    // ---------- group messages by day ----------
    const byDay = React.useMemo(() => {
        const keyOf = (t) => new Date(t).toISOString().slice(0, 10); // YYYY-MM-DD
        const isToday = (key) => key === new Date().toISOString().slice(0, 10);
        const label = (key) =>
            isToday(key)
                ? 'today'
                : new Date(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

        const out = [];
        let lastKey = '';
        for (const m of activeMsgs) {
            const at = m.at || Date.now();
            const k = keyOf(at);
            if (k !== lastKey) {
                out.push({ type: 'day', key: k, label: label(k) });
                lastKey = k;
            }
            out.push({ type: 'msg', msg: m });
        }
        return out;
    }, [activeMsgs]);

    return (
        <div className="message-pageContainer">
            {/* Back to Dashboard */}
            <button type="button" className="Message-dashboard-button" onClick={() => navigate('/Dashboard')}>
                <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
            </button>

            <Sheet variant="plain" className="messagesLayout">
                {/* Left: Friends / Requests */}
                <Sheet
                    variant="soft"
                    sx={{
                        position: 'relative',   // anchor absolute profile button
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        p: 1,
                        display: 'grid',
                        gridTemplateRows: 'auto auto 1fr'
                    }}
                >
                    <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, pl: 15 }}>
                        <Typography level="title-md" sx={{ flex: 1 }}>Messages</Typography>
                        <IconButton variant="outlined" size="sm" onClick={() => setOpenAdd(true)} title="Add friend">
                            <PersonAddAlt1Icon />
                        </IconButton>
                        <Badge badgeContent={incoming.length} color="primary" size="sm">
                            {/* Desktop: Mail icon */}
                            <IconButton variant="outlined" size="sm" title="Requests" className="MailIcon-desktop">
                                <MailIcon />
                            </IconButton>

                            {/* Mobile: Profile button */}
                            <button
                                type="button"
                                className="Messages-profile-button profile-mobile"
                                onClick={() => navigate('/Dashboard/ProfilePage')}
                                style={me.avatar ? { backgroundImage: `url(${me.avatar})` } : undefined}
                                title="Profile"
                            >
                                {!me.avatar ? (me.name?.[0]?.toUpperCase() || 'U') : null}
                            </button>
                        </Badge>

                    </Box>

                    {(incoming.length > 0 || outgoing.length > 0) && (
                        <Box sx={{ px: 1, pb: 1 }}>
                            {incoming.length > 0 && (
                                <>
                                    <Typography level="body-sm" sx={{ mb: 0.5 }}>Requests to you</Typography>
                                    <List sx={{ '--List-gap': '4px' }}>
                                        {incoming.map((u) => (
                                            <ListItem key={`in-${u.id}`}>
                                                <ListItemButton className="friendItem" onClick={() => acceptRequest(u.id)}>
                                                    <Avatar size="sm">{(u.name || u.email || u.id)[0]}</Avatar>
                                                    <ListItemContent>
                                                        <Typography level="title-sm">{u.name || u.email}</Typography>
                                                        <Typography level="body-xs" className="lastLine">Tap to accept</Typography>
                                                    </ListItemContent>
                                                    <IconButton size="sm" variant="soft" color="success"
                                                        onClick={(e) => { e.stopPropagation(); acceptRequest(u.id); }}>
                                                        <DoneIcon />
                                                    </IconButton>
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}

                            {outgoing.length > 0 && (
                                <>
                                    <Typography level="body-sm" sx={{ mt: 0.5, mb: 0.5 }}>Requests you sent</Typography>
                                    <List sx={{ '--List-gap': '4px' }}>
                                        {outgoing.map((u) => (
                                            <ListItem key={`out-${u.id}`}>
                                                <ListItemButton className="friendItem" disabled>
                                                    <Avatar size="sm">{(u.name || u.email || u.id)[0]}</Avatar>
                                                    <ListItemContent>
                                                        <Typography level="title-sm">{u.name || u.email}</Typography>
                                                        <Typography level="body-xs" className="lastLine">Pending…</Typography>
                                                    </ListItemContent>
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Box>
                    )}

                    {/* Friends list */}
                    <List sx={{ '--List-gap': '4px', overflow: 'auto', px: 1, pt: 4 }} className="friendsList">
                        {friends.map((f) => (
                            <ListItem key={f.id}>
                                <ListItemButton
                                    selected={activeId === f.id}
                                    onClick={() => setActiveId(f.id)}
                                    className="friendItem"
                                >
                                    <Avatar size="sm" src={f.avatar}>{(f.name || f.id)[0]}</Avatar>
                                    <ListItemContent>
                                        <Typography level="title-sm">{f.name || f.id}</Typography>
                                        <Typography level="body-xs" noWrap className="lastLine">
                                            {(threads[f.id]?.slice(-1)[0]?.text) || 'Start chatting…'}
                                        </Typography>
                                    </ListItemContent>
                                </ListItemButton>
                            </ListItem>
                        ))}
                        {friends.length === 0 && (
                            <Box sx={{ p: 1.5, color: 'text.tertiary' }}>
                                <Typography level="body-sm">No friends yet. Click “+” to add someone by email.</Typography>
                            </Box>
                        )}
                    </List>

                    {/* Profile button (bottom-left) */}
                    <button
                        type="button"
                        className="Messages-profile-button"
                        onClick={() => navigate('/Dashboard/ProfilePage')}
                        style={me.avatar ? { backgroundImage: `url(${me.avatar})` } : undefined}
                        title="Profile"
                    >
                        {!me.avatar ? (me.name?.[0]?.toUpperCase() || 'U') : null}
                    </button>
                </Sheet>

                {/* Right: Conversation */}
                <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr auto', height: '100%', minHeight: 0, bgcolor: 'background.body' }}>
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar size="sm" src={friends.find((f) => f.id === activeId)?.avatar}>
                                {(friends.find((f) => f.id === activeId)?.name || activeId || '—')[0]}
                            </Avatar>
                            <Typography level="title-md">
                                {friends.find((f) => f.id === activeId)?.name || activeId || '—'}
                            </Typography>
                        </Box>

                        {!canChat && activeId && (
                            <Typography level="body-sm" color="warning" sx={{ mt: 0.5 }}>
                                You aren’t friends yet. Accept the request or ask them to accept yours.
                            </Typography>
                        )}
                    </Box>
                    <Divider />

                    <Box sx={{ p: 2, overflow: 'auto', minHeight: 0 }} className="messagesScroll">
                        <Stack spacing={1}>
                            {byDay.map((item, i) => {
                                if (item.type === 'day') {
                                    return (
                                        <Divider key={`day-${i}`} sx={{ my: 1.5 }}>
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                — {item.label} —
                                            </Typography>
                                        </Divider>
                                    );
                                }

                                const m = item.msg;
                                const mine = String(m.from) === String(me.id);

                                return (
                                    <Box
                                        key={`m-${i}`}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: mine ? 'flex-end' : 'flex-start',
                                            gap: '3px',
                                        }}
                                    >
                                        <Typography level="body-xs" className="messageSender" sx={{ fontWeight: 600 }}>
                                            {mine ? 'You' : (friends.find((f) => String(f.id) === String(m.from))?.name || m.from)}
                                        </Typography>
                                        <Sheet
                                            className={`messageBubble ${mine ? 'mine' : 'theirs'}`}
                                            sx={{
                                                maxWidth: '65%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: mine ? 'flex-end' : 'flex-start',
                                                gap: '3px',
                                            }}
                                        >
                                            <Typography level="body-m" className="messageText">
                                                {m.text}
                                            </Typography>
                                            <Typography level="body-xs" className="messageTime">
                                                {new Date(m.at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                        </Sheet>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                    <Divider />

                    {/* Composer */}
                    <Stack direction="row" spacing={1} sx={{ p: 1.5, backgroundColor: 'background.body' }}>
                        <Input
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder={canChat ? 'Type a message…' : 'Add/accept friend to chat'}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                            sx={{ flex: 1 }}
                            disabled={!canChat}
                            slotProps={{ input: { ref: inputElRef } }}   // ref to native input for caret insert
                        />

                        <IconButton
                            variant="outlined"
                            onClick={(e) => setEmojiAnchor(e.currentTarget)}
                            disabled={!canChat}
                            title="Insert emoji"
                        >
                            <EmojiEmotionsIcon />
                        </IconButton>

                        <Popover
                            open={Boolean(emojiAnchor)}
                            anchorEl={emojiAnchor}
                            onClose={() => setEmojiAnchor(null)}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        >
                            <Box
                                p={1}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(8, 28px)',
                                    gap: 0.75,
                                    placeItems: 'center',
                                }}
                            >
                                {EMOJIS.map((emo) => (
                                    <button
                                        key={emo}
                                        className="emojiBtn"
                                        onClick={() => { insertEmojiAtCursor(emo); setEmojiAnchor(null); }}
                                        aria-label={`Insert ${emo}`}
                                        title={emo}
                                    >
                                        {emo}
                                    </button>
                                ))}
                            </Box>
                        </Popover>

                        <IconButton variant="solid" color="primary" onClick={send} disabled={!canChat}>
                            <SendIcon />
                        </IconButton>
                    </Stack>
                </Box>
            </Sheet>

            {/* Add Friend Modal */}
            <Modal open={openAdd} onClose={() => setOpenAdd(false)}>
                <ModalDialog>
                    <DialogTitle>Add friend</DialogTitle>
                    <DialogContent>
                        Enter their account email. If they already sent you a request, this will auto-accept.
                        <Input
                            sx={{ mt: 1 }}
                            placeholder="friend@example.com"
                            type="email"
                            value={addEmail}
                            onChange={(e) => setAddEmail(e.target.value)}
                        />
                        {err && <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>{err}</Typography>}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="plain" onClick={() => setOpenAdd(false)}>Cancel</Button>
                        <Button loading={busy} onClick={submitAddFriend}>Send</Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </div>
    );
}














